import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { turndropType } from "app/data/static-load/model";
import {
  getAirportCodeForDrop,
  TerminalLocationInfo,
} from "../utils/utils";
import { ReactComponent as DotIcon } from "images/dots.svg";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormInput from "ui-components/form/input/xgsFormInput";
import "./dropsTable.scss";
import { ReactComponent as DeleteIcon } from "images/trash.svg";

export type DropRow = {
  id: string;
  dropType?: string;
  terminalNumber?: string | number;
  miles?: string | number;
  earlyArrivalTime?: string;
  earlyDepartureTime?: string;
  tractorNumber?: string;
};

type DropsTableProps = {
  drops: DropRow[];
  terminalMap: Record<string, string>;
  turnNumber?: string;
  terminalsByAirportCode?: Record<string, TerminalLocationInfo>;
  isEditMode: boolean;
  onSaveDrop: (
    value: string,
    dropId: string,
    type: "tractorNumber" | "earlyArrivalTime" | "earlyDepartureTime"
  ) => void;
  onReorder?: (reorderedDrops: DropRow[]) => void;
};

const HEADER_COLUMNS = [
  { label: "Type", key: "type" },
  { label: "Location", key: "location" },
  { label: "Miles", key: "miles" },
  { label: "Estimated Arrival", key: "arrival" },
  { label: "Estimated Departure", key: "departure" },
  { label: "Tractor", key: "tractor" },
  { label: "Action", key: "action" },
];

const TYPE_BADGE_CLASS: Record<string, string> = {
  O: "origin",
  F: "destination",
  S: "intermediate",
};

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const formatMiles = (miles?: string | number) => {
  if (miles === undefined || miles === null || miles === "") return "-";
  const value = String(miles);
  return value.toLowerCase().includes("mi") ? value : `${value} mi`;
};

const formatLocationLabel = (
  airportCode: string,
  terminalName: string,
  terminalNumber: string | number
) => (
  <>
    {airportCode} – {terminalName}{" "}
    <span className="drops-table__terminal-number">{terminalNumber}</span>
  </>
);

const formatLocation = (
  terminalNumber: string | number | undefined,
  terminalMap: Record<string, string>,
  options?: {
    dropType?: string;
    turnNumber?: string;
    terminalsByAirportCode?: Record<string, TerminalLocationInfo>;
  }
) => {
  const value = terminalMap[terminalNumber?.toString() || ""];
  if (value) {
    const parts = value.split(" - ");
    if (parts.length >= 3 && parts[0] && parts[1]) {
      return formatLocationLabel(parts[0], parts[1], parts[2]);
    }
  }

  const airportCode = getAirportCodeForDrop(options?.dropType, options?.turnNumber);
  if (airportCode && options?.terminalsByAirportCode) {
    const terminal = options.terminalsByAirportCode[airportCode.toUpperCase()];
    if (terminal) {
      return formatLocationLabel(
        terminal.airportCode,
        terminal.terminalName.toLowerCase().capitalize(),
        terminal.terminalNumber
      );
    }
  }

  if (airportCode) {
    if (terminalNumber != null && terminalNumber !== "") {
      return formatLocationLabel(airportCode, airportCode, terminalNumber);
    }

    return airportCode;
  }

  return "-";
};

const getTypeLabel = (dropType?: string) =>
  turndropType.find((item) => item.value === dropType)?.label ?? "-";


const DropsTableHeader: React.FC = () => (
  <div className="drops-table__header">
    {HEADER_COLUMNS.map((column) => (
      <div key={column.key} className="drops-table__header-cell">
        {column.label}
      </div>
    ))}
  </div>
);

type DropsTableRowProps = {
  drop: DropRow;
  terminalMap: Record<string, string>;
  turnNumber?: string;
  terminalsByAirportCode?: Record<string, TerminalLocationInfo>;
  isEditMode: boolean;
  onSaveDrop: DropsTableProps["onSaveDrop"];
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: React.HTMLAttributes<HTMLDivElement>;
  style?: React.CSSProperties;
};

const DropsTableRow: React.FC<DropsTableRowProps> = ({
  drop,
  terminalMap,
  turnNumber,
  terminalsByAirportCode,
  isEditMode,
  onSaveDrop,
  isDragging,
  dragHandleProps,
  innerRef,
  draggableProps,
  style,
}) => {
  const badgeClass = TYPE_BADGE_CLASS[drop.dropType ?? ""] ?? "origin";
  const typeLabel = getTypeLabel(drop.dropType);

  return (
    <div
      ref={innerRef}
      {...draggableProps}
      {...(isEditMode ? dragHandleProps : {})}
      style={style}
      className={[
        "drops-table__row",
        isDragging ? "drops-table__row--dragging" : "",
        isEditMode ? "drops-table__row--editable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="drops-table__cell">

        {drop.dropType ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: "10px", cursor: "grab" }}>
              <DotIcon />
            </span>
            <span className={`drops-table__type-badge drops-table__type-badge--${badgeClass}`}>
              {typeLabel}
            </span>
          </div>
        ) : (
          "-"
        )}
      </div>
      <div className="drops-table__cell">
        {formatLocation(drop.terminalNumber, terminalMap, {
          dropType: drop.dropType,
          turnNumber,
          terminalsByAirportCode,
        })}
      </div>
      <div className="drops-table__cell">{formatMiles(drop.miles)}</div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSFormInput
            type="text"
            name="earlyArrivalTime"
            labelMode={LabelModes.column}
            className="drops-table__cell"
            value={drop.earlyArrivalTime}
            onChange={(e) => onSaveDrop(e.currentTarget.value as string, drop.id, "earlyArrivalTime")}
          />
        ) : (
          drop.earlyArrivalTime || "-"
        )}
      </div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSFormInput
            type="text"
            name="earlyDepartureTime"
            labelMode={LabelModes.column}
            className="drops-table__cell"
            value={drop.earlyDepartureTime}
            onChange={(e) => onSaveDrop(e.currentTarget.value as string, drop.id, "earlyDepartureTime")}
          />
        ) : (
          drop.earlyDepartureTime || "-"
        )}
      </div>
      <div className="drops-table__cell">
        {isEditMode ? (
          <XGSFormInput
            type="text"
            name="tractorNumber"
            labelMode={LabelModes.column}
            className="drops-table__cell"
            value={drop.tractorNumber}
            onChange={(e) => onSaveDrop(e.currentTarget.value as string, drop.id, "tractorNumber")}
          />
        ) : (
          drop.tractorNumber || "-"
        )}
      </div>
      <div className="drops-table__cell drops-table__cell--action">
        {isEditMode && (
          <div onClick={(e) => { e.stopPropagation(); }}>
            <DeleteIcon className="clone-week-btn__delete_icon" />
          </div>
        )}
      </div>
    </div>
  );
};

const DropsTable: React.FC<DropsTableProps> = ({
  drops,
  terminalMap,
  turnNumber,
  terminalsByAirportCode,
  isEditMode,
  onSaveDrop,
  onReorder,
}) => {
  const [orderedDrops, setOrderedDrops] = useState<DropRow[]>(drops);

  useEffect(() => {
    setOrderedDrops(drops);
  }, [drops]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !isEditMode) return;
    if (result.source.index === result.destination.index) return;

    const reordered = reorder(orderedDrops, result.source.index, result.destination.index);
    setOrderedDrops(reordered);
    onReorder?.(reordered);
  };

  return (
    <div className="drops-table">
      <DropsTableHeader />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="lane-drops">
          {(provided) => (
            <div
              className="drops-table__body"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {orderedDrops.length === 0 ? (
                <div className="drops-table__empty">No drops available</div>
              ) : (
                orderedDrops.map((drop, index) => (
                  <Draggable
                    key={drop.id}
                    draggableId={`${"lane-drops"}-${drop.id}`}
                    index={index}
                    isDragDisabled={!isEditMode}
                  >
                    {(draggableProvided, snapshot) => (
                      <DropsTableRow
                        drop={drop}
                        terminalMap={terminalMap}
                        turnNumber={turnNumber}
                        terminalsByAirportCode={terminalsByAirportCode}
                        isEditMode={isEditMode}
                        onSaveDrop={onSaveDrop}
                        isDragging={snapshot.isDragging}
                        innerRef={draggableProvided.innerRef}
                        draggableProps={draggableProvided.draggableProps}
                        dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                        style={draggableProvided.draggableProps.style}
                      />
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DropsTable;
